import { createTheme } from '@mui/material/styles';

const color = {
  primary: {
    primary0: '#F9F9FA',
    primary100: '#EBEEEE',
    primary200: '#DFE5E6',
    primary300: '#CCD4D5',
    primary400: '#B8C4C6',
    primary500: '#A6B3B6',
    primary600: '#909FA4',
    primary700: '#77888E',
    primary800: '#576B72',
    primary900: '#425157'
  },
  grey: {
    grey0: '#F9FAFC',
    grey50: '#F2F2F3',
    grey100: '#E6ECF2',
    grey200: '#C3CED9',
    grey300: '#A9B7C4',
    grey400: '#9AA9B8',
    grey500: '#708394',
    grey600: '#5C6B7F',
    grey700: '#42526B',
    grey800: '#2D3C57',
    grey900: '#222E4A'
  },
  system: {
    system0: '#F0F8FF',
    system100: '#D9EDFE',
    system200: '#B3D8FD',
    system300: '#8DBFFB',
    system400: '#70A8F8',
    system500: '#4285F4',
    system600: '#3066D1',
    system700: '#214BAF',
    system800: '#15348D',
    system900: '#0C2375'
  },
  media: {
    facebook: {
      main: '#1877F2',
      light: '#BAD3FB',
      dark: '#0D65D6',
      facebook01: '#DCE7FA'
    },
    google: {
      // main: '#FBBC04',
      main: '#FBAD04',
      light: '#FCD04F',
      dark: '#F0980C',
      google01: '#FAF2DC'
    },
    line: {
      main: '#4ACD41',
      light: '#C8F0C6',
      dark: '#107B10',
      line01: '#DEFBDC'
    },
    yahoo: {
      main: '#B960CA',
      light: '#EACFEF',
      dark: '#9930F0',
    },
    taboola: {
      main: '#004B7A',
      light: '#b1e0f0',
      dark: '#013556',
      taboola01: '#e0f3f9',
      taboola02: '#F0F8FF'
    }
  },
}

const fontSize = {
  lg: '1.25rem',
  md: '1.125rem',
  sm: '0.875rem',
  xs: '0.75rem'
};

const theme = createTheme({
  palette: {
    background: {
      default: '#FDFDFD',
      paper: '#ffffff'
    },
    // action: {
    //   disabled: color.grey.grey400
    // },
    primary: {
      main: color.primary.primary600,
      light: color.primary.primary300,
      dark: color.primary.primary800,
      primary0: color.primary.primary0,
      primary100: color.primary.primary100,
      primary200: color.primary.primary200,
      primary300: color.primary.primary300,
      primary400: color.primary.primary400,
      primary500: color.primary.primary500,
      primary600: color.primary.primary600,
      primary700: color.primary.primary700,
      primary800: color.primary.primary800,
      primary900: color.primary.primary900
    },
    secondary: {
      main: '#FFAA00',
    },
    error: {
      main: '#FF5C5C'
    },
    warning: {
      main: '#FF5C5C'
    },
    success: {
      main: '#00CC9B'
    },
    text: {
      primary: '#444444',
    },
    greyOutlined: {
      main: color.grey.grey300
    },
    grey: {
      main: color.grey.grey600,
      light: color.grey.grey300,
      dark: color.grey.grey800,
      w0: color.grey.grey0,
      w50: color.grey.grey50,
      w100: color.grey.grey100,
      w200: color.grey.grey200,
      w300: color.grey.grey300,
      w400: color.grey.grey400,
      w500: color.grey.grey500,
      w600: color.grey.grey600,
      w700: color.grey.grey700,
      w800: color.grey.grey800,
      w900: color.grey.grey900
    },
    system: {
      main: color.system.system600,
      light: color.system.system300,
      dark: color.system.system800,
      w0: color.system.system0,
      w100: color.system.system100,
      w200: color.system.system200,
      w300: color.system.system300,
      w400: color.system.system400,
      w500: color.system.system500,
      w600: color.system.system600,
      w700: color.system.system700,
      w800: color.system.system800,
      w900: color.system.system900
    },
    facebook: {
      main: color.media.facebook.main,
      light: color.media.facebook.light,
      dark: color.media.facebook.dark,
      facebook01: color.media.facebook.facebook01
    },
    google: {
      main: color.media.google.main,
      light: color.media.google.light,
      dark: color.media.google.dark,
      google01: color.media.google.google01
    },
    line: {
      main: color.media.line.main,
      light: color.media.line.light,
      dark: color.media.line.dark,
      line01: color.media.line.line01,
    },
    yahoo: {
      main: color.media.yahoo.main,
      light: color.media.yahoo.light,
      dark: color.media.yahoo.dark
    },
    taboola: {
      main: color.media.taboola.main,
      light: color.media.taboola.light,
      dark: color.media.taboola.dark,
      taboola01: color.media.taboola.taboola01,
    },
  },
  shadows: [
    "none",
    "0px 0px 16px rgba(107, 117, 128, 0.15)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 0.3)",
    ...Array(17).fill('none')
  ],
  typography: {
    fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    h1: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '2rem',
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '16px',
    },
    h2: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.5rem',
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '16px',
    },
    h3: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.25rem',
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '16px',
    },
    h3_normal: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.25rem',
      marginTop: '16px',
      marginBottom: '16px',
    },
    h4: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.125rem',
      fontWeight: 600,
      marginTop: '8px',
      marginBottom: '8px',
    },
    h4_normal: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.125rem',
      fontWeight: 400,
      marginTop: '8px',
      marginBottom: '8px',
    },
    h5: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1rem',
      fontWeight: 600,
      marginTop: '8px',
      marginBottom: '8px',
    },
    h6: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '0.875rem',
      fontWeight: 600,
      marginTop: '8px',
      marginBottom: '0',
    },
    subtitle1: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontWeight: 600,
      lineHeight: 1.5
    },
    subtitle2: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '0.875rem',
      fontWeight: 400,
    },
    body1: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontWeight: 400,
    },
    body_lg: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.25rem'
    },
    body_base: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    },
    body_md: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1.125rem'
    },
    body_sm: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '0.875rem',
    },
    body_xs: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '0.75rem'
    },
    button: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '1rem'
    },
    caption: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
      fontSize: '0.75rem'
    },
    overline: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    },
  },
  borderRadius: {
    small: '4px',
    normal: '8px',
    md: '16px'
  },
  customFontSize: {
    lg: fontSize.lg,
    md: fontSize.md,
    sm: fontSize.sm,
    xs: fontSize.xs
  },
  components: {
    MuiStack: {
      defaultProps: {
        direction: 'row',
        flexWrap: 'wrap',
      },
      variants: [
        {
          props: { variant: 'center-center' },
          style: {
            justifyContent: 'center',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'start-center' },
          style: {
            justifyContent: 'flex-start',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'end-center' },
          style: {
            justifyContent: 'flex-end',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'arround-center' },
          style: {
            justifyContent: 'space-arround',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'between-center' },
          style: {
            justifyContent: 'space-between',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'center-start' },
          style: {
            justifyContent: 'center',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'start-start' },
          style: {
            justifyContent: 'start',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'between-start' },
          style: {
            justifyContent: 'space-between',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'start-end' },
          style: {
            justifyContent: 'start',
            alignItems: 'end'
          },
        },
      ]
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: '20px',
          height: '20px',
          position: 'relative'
        },
      },
      variants: [
        {
          props: { variant: 'dark' },
          style: {
            color: color.grey.grey600,
            top: '2px',
            width: '16px',
            height: '16px',
            marginLeft: '4px'
          },
        },
        {
          props: { variant: 'subTitle' },
          style: {
            marginRight: '4px',
            verticalAlign: 'sub'
          },
        },
        {
          props: { variant: 'lg_grey' },
          style: {
            width: '40px',
            height: '40px',
            color: color.grey.grey600
          },
        },
        {
          props: { variant: 'grey_mt2' },
          style: {
            marginRight: '8px',
            marginTop: '2px',
            color: color.grey.grey400
          },
        }
      ],
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        // disableElevation: true
      },
      variants: [
        {
          props: { variant: 'text-cancel' },
          style: {
            marginRight: '8px',
            height: '40px',
            color: color.grey.grey600
          },
        },
        {
          props: { variant: 'text-system-normal' },
          style: {
            marginBottom: '8px',
            padding: '2px 4px',
            color: color.system.system600,
            '&:hover': {
              backgroundColor: color.system.system0,
            }
          },
        },
        {
          props: { variant: 'text-taboola' },
          style: {
            marginBottom: '8px',
            padding: '2px 4px',
            color: color.media.taboola.main,
            '&:hover': {
              backgroundColor: color.media.taboola.taboola02,
            }
          },
        },
        {
          props: { variant: 'text-grey-normal' },
          style: {
            margin: '4px',
            padding: 0,
            color: color.grey.grey600,
            fontSize: fontSize.sm,
            '&:hover': {
              backgroundColor: 'transparent',
              color: color.grey.grey900,
            },

            '& .MuiSvgIcon-root': {
              marginRight: '4px'
            }
          },
        },
        {
          props: { variant: 'outlined-square' },
          style: {
            minWidth: 'auto',
            width: '40px',
            height: '40px',
            padding: 0,
            border: `1px solid ${color.grey.grey300}`
          },
        },
        {
          props: { variant: 'fill-create' },
          style: {
            minWidth: 'auto',
            height: '40px',
            backgroundColor: color.primary.primary600,

            '&:hover': {
              backgroundColor: color.primary.primary800,
            }
          },
        },
        {
          props: { variant: 'auth-outlined' },
          style: {
            height: '40px',
            border: `1px solid ${color.grey.grey500}`,
            color: color.grey.grey500,

            '& img': {
              width: '20px',
              height: '20px',
              marginRight: '8px'
            },

            '&:hover': {
              backgroundColor: color.system.system0,
              borderColor: color.system.system500,
              color: color.system.system500,
            }
          },
        },
      ],
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.MuiButton-contained': {
            color: '#FFF',
            boxShadow: 'none',

            '&:hover': {
              boxShadow: 'none',
            }
          },
          '&.MuiChip-root': {
            height: '22px',
            margin: '1px 2px 1px 0',

            '&.MuiChip-colorPrimary': {
              color: '#fff',

              '& .MuiChip-deleteIcon': {
                width: '16px',
                height: '16px',
                color: 'rgba(255, 255, 255, 0.7)',

                '&:hover': {
                  color: '#fff'
                }
              }
            }
          },
        },
      },
      defaultProps: {
        disableRipple: true,
        // disableElevation: true
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          height: '36px',

          '& .MuiTableSortLabel-icon': {
            position: 'absolute',
            left: '100%',
            width: '16px',
            height: '16px',
            margin: 0
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',

          '&.MuiTableRow-hover:hover': {
            backgroundColor: color.grey.grey0,
          },

          '&.Mui-selected': {
            backgroundColor: color.grey.grey0
          },

          '& .MuiTypography-root': {
            fontSize: 'inherit'
          },

          '& .MuiListItemText-root': {
            margin: 0
          },

          '&:last-child td': {
            borderBottom: 'none'
          }



        },
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          height: '40px',
          lineHeight: '40px'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
          '& .MuiInputBase-input': {
            height: '40px',
            padding: '0 8px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border .5s',
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: color.grey.grey600,
            }
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
            }
          },
        },
        notchedOutline: {
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            width: '100%',
            padding: '8px',

            '& .MuiAutocomplete-input': {
              height: '24px',
              padding: 0
            }
          },

          '& .MuiChip-root': {
            height: '22px',
            margin: '1px 2px 1px 0',
            backgroundColor: color.primary.primary600,
            color: '#fff',

            '& .MuiChip-deleteIcon': {
              width: '16px',
              height: '16px',
              color: 'rgba(255, 255, 255, 0.7)',

              '&:hover': {
                color: '#fff'
              }
            }
          },
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {

        },
      },
      variants: [
        {
          props: { variant: 'rounded-rect' },
          style: {
            backgroundColor: 'transparent',
            border: `1px solid ${color.grey.grey600}`,
            borderRadius: '4px',
            fontSize: 'inherit'
          },
        },
      ],
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }
      }
    },
  }

});

export default theme;